import { SelectedPathIds } from '@msdk/common'
import { AreaGroup, CustomItem, EntityItem, SitemapItem, SitemapReferenceItem, ViewItem } from '@msdk/definitions'

export function SelectedPathIdsAreDifferent(a: SelectedPathIds, b: SelectedPathIds): boolean {
  if (a.ids.length !== b.ids.length) {
    return true
  }

  for (let i = 0; i < a.ids.length; i++) {
    if (b.ids.indexOf(a.ids[i]) === -1) {
      return true
    }
  }

  return false
}

export function findSelectedIds(
  sitemapModelAreas: AreaGroup[],
  defaultSitemapAreas: AreaGroup[],
  sitemapItems: SitemapReferenceItem[],
  entityName?: string,
  moduleName?: string,
  isCustom?: boolean,
): SelectedPathIds | undefined {
  if (!moduleName) {
    return undefined
  }

  if (!isCustom && !entityName) {
    return undefined
  }

  const entityNameOrCustom = entityName ?? 'CUSTOM'

  const areas = sitemapModelAreas.concat(defaultSitemapAreas)
  for (const area of areas) {
    const res = isMatching(area, sitemapItems, entityNameOrCustom, moduleName)
    if (res.length > 0) {
      return { ids: res }
    }
  }

  return undefined
}

function mapReferenceItemToPairs(item: SitemapReferenceItem) {
  const results: string[] = []
  item.entries.forEach((pair) => {
    results.push(`${pair.entity}:${pair.viewName}`)
  })
  return results.join(',')
}

function isMatchingItem(
  item: ViewItem,
  sitemapItems: SitemapReferenceItem[],
  entityName: string,
  moduleName: string,
): boolean {
  if (item.entity && item.viewName) {
    return entityName === item.entity && moduleName === item.viewName
  } else {
    const referenceItem = sitemapItems.find((i) => i.id === item.id)
    return entityName === referenceItem?.entries[0].entity && moduleName === mapReferenceItemToPairs(referenceItem)
  }
}

function isMatchingCustomItem(item: CustomItem, moduleName: string): boolean {
  return moduleName === item.name
}

function isMatchingEntityItem(item: EntityItem, entityName: string, moduleName: string): boolean {
  return entityName === item.entity && moduleName === item.formName
}

function isMatching(
  item: SitemapItem,
  sitemapItems: SitemapReferenceItem[],
  entityName: string,
  moduleName: string,
): string[] {
  if (
    item.type === 'ViewItem' ||
    item.type === 'ExternalUrl' ||
    item.type === 'SmartAppItem' ||
    item.type === 'SmartAppItemUr'
  ) {
    if (isMatchingItem(item as ViewItem, sitemapItems, entityName, moduleName)) {
      return [item.id]
    }
  }

  if (item.type === 'CustomItem') {
    if (isMatchingCustomItem(item as CustomItem, moduleName)) {
      return [item.id]
    }
  }

  if (item.type === 'EntityItem') {
    if (isMatchingEntityItem(item as EntityItem, entityName, moduleName)) {
      return [item.id]
    }
  }

  if (item.type === 'AreaGroup') {
    for (const child of (item as AreaGroup).items) {
      const result = isMatching(child, sitemapItems, entityName, moduleName)
      if (result.length > 0) {
        return [item.id, ...result]
      }
    }
  }

  return []
}

export function isAreaOpen(area: AreaGroup, currentOpenId?: string): boolean {
  if (currentOpenId === area.id) return true
  return !!area.items.find((i) => {
    return i.type === 'AreaGroup' && isAreaOpen(i as AreaGroup, currentOpenId)
  })
}

export function findParentAreaId(id: string, items: AreaGroup[], parentId?: string): string | undefined {
  for (const item of items) {
    if (item.id === id) {
      return parentId
    }

    if (item.items) {
      const res = findParentAreaId(id, (item.items ?? []) as AreaGroup[], item.id)
      if (res !== undefined) {
        return res
      }
    }
  }

  return undefined
}

export function findAreaById(id: string, items: AreaGroup[]): AreaGroup | undefined {
  for (const item of items) {
    if (item.id === id) {
      return item
    }

    if (item.items) {
      const res = findAreaById(id, (item.items ?? []) as AreaGroup[])
      if (res) {
        return res
      }
    }
  }

  return undefined
}
